import { useReducedMotion } from '@/lib/useReducedMotion';
import { useEffect, useRef, type ReactNode } from 'react';

interface IrisProps {
  /** Diameter in px. */
  size: number;
  /** Change this value to snap the iris shut and open it again. */
  cycle?: string | number;
  /** How far the iris opens, 0..1. */
  openTo?: number;
  /** Seconds for a full open. */
  duration?: number;
  /** Draw as a letter "o": a solid ring the weight of the type, no scale ticks. */
  glyph?: boolean;
  children?: ReactNode;
  className?: string;
}

const TAU = Math.PI * 2;
const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -9 * t));

/**
 * The 7-blade iris from the Aperture concept, recoloured to the navy/ice palette.
 * Whatever is passed as children sits behind the blades and is revealed as it opens.
 */
export const Iris = ({ size, cycle, openTo = 1, duration = 1.75, glyph = false, children, className = '' }: IrisProps) => {
  const reduced = useReducedMotion();
  const cvRef = useRef<HTMLCanvasElement>(null);
  const startRef = useRef(0);

  useEffect(() => {
    startRef.current = performance.now();
  }, [cycle]);

  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const c = cv.getContext('2d');
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = Math.round(size * dpr);
    cv.height = Math.round(size * dpr);
    if (!startRef.current) startRef.current = performance.now();
    let raf = 0;

    const draw = (now: number) => {
      raf = 0;
      // rAF timestamps can precede the performance.now() start mark by a few ms.
      const time = Math.max(0, (now - startRef.current) / 1000);
      const e = reduced ? openTo : easeOutExpo(Math.min(1, time / duration)) * openTo;
      const S = size;
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.clearRect(0, 0, S, S);
      const barrel = S * 0.5;
      const gap = glyph ? S * 0.13 : Math.max(6, Math.min(S * 0.075, S * 0.055 + 6));
      const Rb = barrel - gap;
      const breathe = reduced ? 1 : 1 + 0.013 * Math.sin(now / 1000 * 0.55);
      const rOpen = Rb * 0.835, rShut = Rb * 0.032;
      const r = Math.max(0.5, (rShut + (rOpen - rShut) * e) * breathe);
      const rot = (1 - e) * 0.66 + (reduced ? 0 : (now / 1000) * 0.014);
      const Rout = Rb * 2.0, N = 7;

      c.save();
      c.translate(S / 2, S / 2);

      c.save();
      c.beginPath(); c.arc(0, 0, Rb, 0, TAU); c.clip();
      for (let i = 0; i < N; i++) {
        c.save();
        c.rotate(rot + i * (TAU / N));
        const L = r * Math.tan(Math.PI / N) * 1.95 + Rb * 0.15;
        const bow = r * 0.16;
        const g = c.createLinearGradient(r, 0, Rb * 1.05, 0);
        if (i % 2) { g.addColorStop(0, '#1C2548'); g.addColorStop(0.5, '#10173A'); }
        else { g.addColorStop(0, '#18204A'); g.addColorStop(0.5, '#0C1332'); }
        g.addColorStop(1, '#060B1E');
        c.beginPath();
        c.moveTo(r, -L);
        c.quadraticCurveTo(r + bow, 0, r, L);
        c.lineTo(Rout, L * 1.75);
        c.lineTo(Rout, -L * 1.75);
        c.closePath();
        c.fillStyle = g; c.fill();
        c.beginPath(); c.moveTo(r, -L); c.quadraticCurveTo(r + bow, 0, r, L);
        c.lineWidth = 2.4; c.strokeStyle = 'rgba(143,227,240,.16)'; c.stroke();
        c.lineWidth = 1; c.strokeStyle = 'rgba(214,226,255,.16)'; c.stroke();
        c.restore();
      }
      const gg = c.createRadialGradient(0, 0, r * 0.8, 0, 0, r * 1.16);
      gg.addColorStop(0, 'rgba(143,227,240,0)');
      gg.addColorStop(0.62, 'rgba(143,227,240,.10)');
      gg.addColorStop(1, 'rgba(143,227,240,0)');
      c.globalCompositeOperation = 'lighter';
      c.fillStyle = gg;
      c.beginPath(); c.arc(0, 0, r * 1.16, 0, TAU); c.fill();
      c.globalCompositeOperation = 'source-over';
      c.restore();

      if (glyph) {
        c.lineWidth = S * 0.1;
        c.strokeStyle = '#EDEEF2';
        c.beginPath(); c.arc(0, 0, Rb + S * 0.055, 0, TAU); c.stroke();
        c.restore();
        if (!reduced && !document.hidden) raf = requestAnimationFrame(draw);
        return;
      }

      c.lineWidth = 1;
      c.strokeStyle = 'rgba(214,226,255,.16)';
      c.beginPath(); c.arc(0, 0, Rb + 1.5, 0, TAU); c.stroke();
      c.strokeStyle = 'rgba(214,226,255,.06)';
      c.beginPath(); c.arc(0, 0, barrel - 1, 0, TAU); c.stroke();
      c.strokeStyle = 'rgba(168,85,247,.28)'; c.lineWidth = 1.4;
      c.beginPath(); c.arc(0, 0, Rb + 1.5, Math.PI * 0.62, Math.PI * 1.02); c.stroke();

      const t0 = Rb + 4, tickN = 60;
      for (let k = 0; k < tickN; k++) {
        const a = k * (TAU / tickN) - Math.PI / 2;
        const lng = k % 5 === 0;
        const t1 = t0 + (lng ? gap * 0.52 : gap * 0.3);
        c.strokeStyle = lng ? 'rgba(214,226,255,.2)' : 'rgba(214,226,255,.09)';
        c.lineWidth = lng ? 1.1 : 1;
        c.beginPath();
        c.moveTo(Math.cos(a) * t0, Math.sin(a) * t0);
        c.lineTo(Math.cos(a) * t1, Math.sin(a) * t1);
        c.stroke();
      }
      c.strokeStyle = 'rgba(143,227,240,.9)'; c.lineWidth = 1.6;
      c.beginPath(); c.moveTo(0, -(t0 - 1)); c.lineTo(0, -(t0 + gap * 0.62)); c.stroke();
      c.fillStyle = 'rgba(143,227,240,.9)';
      c.beginPath(); c.arc(0, -(t0 + gap * 0.62) - 2.4, 1.7, 0, TAU); c.fill();
      c.restore();

      if (!reduced && !document.hidden) raf = requestAnimationFrame(draw);
    };
    const onVisibilityChange = () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = 0; }
      else if (!reduced && !raf) raf = requestAnimationFrame(draw);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, [size, openTo, duration, glyph, reduced]);

  return (
    <div className={`relative flex-none ${glyph ? 'inline-block' : ''} ${className}`} style={{ width: size, height: size }}>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
      <canvas ref={cvRef} aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ width: size, height: size }} />
    </div>
  );
};
